import { supabase } from './supabase';

const API_BASE_URL = import.meta.env.VITE_CHATBOT_API_BASE_URL || '';

export const IS_API_CONFIGURED = !!API_BASE_URL;

export interface ChatbotRequest {
  session_id: string | null;
  message: string;
  locale: string;
  country: string | null;
  currency: string | null;
  page_context: {
    page_type: string;
    product_id: string | null;
    category: string | null;
    sku: string | null;
  };
  debug?: boolean;
}

export interface ChatbotResponse {
  session_id: string;
  answer: string;
  suggested_questions: string[];
  safety_status: string;
  quote_request_cta: {
    enabled: boolean;
    label: string;
    url: string;
  };
  sources: unknown[];
  _debug?: {
    has_dashscope_key?: boolean;
    has_deepseek_key?: boolean;
    dashscope_error?: string;
    deepseek_error?: string;
    rag_error?: string;
    matched_chunks?: number;
    category?: string;
    lang?: string;
    [key: string]: unknown;
  };
}

export async function sendChatbotMessage(payload: ChatbotRequest): Promise<ChatbotResponse> {
  const base = API_BASE_URL.replace(/\/$/, '');
  const isEdgeFunction = base.includes('supabase.co/functions/v1');
  const functionName = 'chatbot-api';

  // Always send debug mode to help diagnose issues
  const requestPayload = { ...payload, debug: true };

  // eslint-disable-next-line no-console
  console.debug('[Chatbot] API base:', base, 'isEdgeFunction:', isEdgeFunction);
  // eslint-disable-next-line no-console
  console.debug('[Chatbot] Request locale:', payload.locale, 'page_type:', payload.page_context.page_type);

  // Try supabase.functions.invoke first for Edge Functions (handles auth correctly)
  if (isEdgeFunction && supabase) {
    try {
      // eslint-disable-next-line no-console
      console.log('[Chatbot] Using supabase.functions.invoke for:', functionName);
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: requestPayload,
      });

      if (error) {
        throw new Error(`Chatbot API invoke error: ${error.message}`);
      }

      const responseData = data as ChatbotResponse;
      logResponseDebug(responseData);
      return responseData;
    } catch (invokeErr) {
      // eslint-disable-next-line no-console
      console.error('[Chatbot] supabase.functions.invoke failed:', invokeErr);
      throw new Error(
        `Chatbot API failed: ${invokeErr instanceof Error ? invokeErr.message : 'Unknown error'}`
      );
    }
  }

  // Fallback: direct fetch for non-Edge Function endpoints only
  const url = base ? `${base}/api/chatbot/message` : '/api/chatbot/message';

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || ''}`,
      'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '',
    },
    body: JSON.stringify(requestPayload),
  });

  if (!response.ok) {
    throw new Error(`Chatbot API error: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as ChatbotResponse;
  logResponseDebug(data);
  return data;
}

function logResponseDebug(data: ChatbotResponse): void {
  // eslint-disable-next-line no-console
  console.debug('[Chatbot] Response safety_status:', data.safety_status);
  // eslint-disable-next-line no-console
  console.debug('[Chatbot] Response answer:', data.answer?.substring(0, 100));

  if (data._debug) {
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — has_dashscope_key:', data._debug.has_dashscope_key);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — has_deepseek_key:', data._debug.has_deepseek_key);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — embedding_dim:', data._debug.embedding_dim);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — chunks_found:', data._debug.chunks_found);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — deepseek_called:', data._debug.deepseek_called);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — deepseek_parsed:', data._debug.deepseek_parsed);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — embedding_error:', data._debug.embedding_error);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — deepseek_error:', data._debug.deepseek_error);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — error_reason:', data._debug.error_reason);
    // eslint-disable-next-line no-console
    console.debug('[Chatbot] Debug — full debug object:', data._debug);
  }
}